import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";
import { initializeApp, isNative } from "@/utils/capacitor";
import { initTheme } from "@/lib/theme";

// Apply the stored theme (default light) as early as possible. The inline
// bootstrap in public/index.html already set data-theme before first paint;
// this re-affirms it once the app module loads (and keeps theme-color synced).
initTheme();

// Loading fallback for i18n
const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

// Root component with Capacitor initialization
const Root = () => {
  useEffect(() => {
    // Initialize native features if running as native app
    initializeApp();

    // Add native class to body for platform-specific styling
    if (isNative) {
      document.body.classList.add("native-app");
    }
  }, []);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <App />
    </Suspense>
  );
};

// SEO: drop the prerender-injected canonical + ld+json (marked
// data-prerendered-seo in prerender-seo.js) before React renders, so
// react-helmet-async — which APPENDS link/script tags rather than replacing
// them — is the single source. Without this, every prerendered route ends up
// with 2-3 <link rel=canonical> and duplicate JSON-LD, which confuses crawlers.
// The static tags remain in the served HTML for non-JS crawlers; JS crawlers
// read the settled head (helmet's single canonical).
document
  .querySelectorAll("head [data-prerendered-seo]")
  .forEach((el) => el.remove());

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// Register service worker for PWA + Push Notifications
if ("serviceWorker" in navigator) {
  // Auto-update: when a freshly-deployed SW (new build id — see
  // public/service-worker.js) installs and takes control, reload once so the
  // user gets the new bundle immediately instead of a stale cached one.
  // Guarded to returning visitors (a SW already controls the page) so a
  // first-ever visit doesn't reload; `refreshing` prevents reload loops.
  if (navigator.serviceWorker.controller) {
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  }
  window.addEventListener("load", () => {
    // Cache-bust the SW registration URL with the app's build hash. CF Pages
    // IGNORES our _headers `no-cache` and serves /service-worker.js with a 4h
    // `max-age` (verified in prod), so a byte-new SW isn't re-fetched for hours
    // and a stuck old worker keeps serving the old bundle — the "I don't see the
    // new version" bug. The main JS filename hash changes on every real deploy,
    // so `?v=<hash>` makes each deploy a NEW script URL → a guaranteed cache-miss
    // fetch of the fresh SW → update detected on the next load regardless of the
    // 4h asset cache → skipWaiting/clients.claim → the controllerchange reload
    // above swaps the user onto the new bundle. Stable within a build, so no
    // re-register churn or reload loop.
    const buildTag =
      document
        .querySelector('script[src*="/static/js/main."]')
        ?.src.match(/main\.([A-Za-z0-9]+)\./)?.[1] || "";
    const swUrl = buildTag
      ? `/service-worker.js?v=${buildTag}`
      : "/service-worker.js";
    navigator.serviceWorker
      // updateViaCache:"none" is the fix for "a new deploy doesn't reach
      // returning users". Cloudflare Pages serves /service-worker.js with
      // `Cache-Control: max-age=14400` (its 4h asset default) and OVERRIDES the
      // `no-cache` rule in public/_headers — CF applies our _headers CSP but not
      // the Cache-Control for .js assets (verified in prod). With the default
      // update policy the browser keeps the SW script cached ~4h, so reg.update()
      // re-reads the OLD worker and never sees the new build id → no
      // skipWaiting → the device stays on the stale bundle for up to 4 hours.
      // "none" makes the browser BYPASS the HTTP cache for the SW script on every
      // update check, so a new deploy is detected on the very next load →
      // skipWaiting + clients.claim → the controllerchange handler above reloads
      // into the fresh bundle. This is what makes the server-driven model (§E:
      // one CF deploy = all surfaces current) actually reach returning users.
      .register(swUrl, { updateViaCache: "none" })
      .then((reg) => {
        console.log("[SW] Registered:", reg.scope);
        // Proactively check for an updated SW on every load.
        reg.update().catch(() => {});
      })
      .catch((err) => {
        console.warn("[SW] Registration failed:", err);
      });
  });
}
